import { useFeatureFlag } from "contexts/FeatureFlagContext";
import moment from "moment";
import React from "react";
import { useHistory } from "react-router-dom";
import { ScanStatus, ScanSummary } from "types/api/scanSummary";
import { FilterInfo } from "types/pagination";
import { formatNumber } from "utils/numberUtil";
import { updateURLSearch } from "utils/urlUtil";
import { isSBOMScan } from "views/scanresult/scanUtil";

import CaretDownIcon from "ac-components/ac-icons/CaretDownIcon";
import CaretUpIcon from "ac-components/ac-icons/CaretUpIcon";
import ACPopover from "ac-components/ac-popover/ACPopover";

import "./ScanStats.less";

type Props = {
  record: ScanSummary;
};

export default function ScanStats({ record }: Props) {
  const history = useHistory();
  const featureFlags = useFeatureFlag();

  const {
    findings: {
      totalFindings: { count: totalFindingsCount = 0 } = {},
      newFindings: { count: newFindingsCount = 0 } = {},
      resolvedFindings: { count: resolvedFindingsCount = 0 } = {},
      duplicateFindings: { count: duplicateFindingsCount = 0 } = {},
    } = {},
    id,
  } = record;

  const sbomFlagsEnabled =
    featureFlags.DECOUPLE_SBOM_INGESTION && featureFlags.MANAGE_SBOM;

  const redirectToFindings = (type: string) => {
    const filters: FilterInfo = {};
    let url: string;

    if (record.product?.id) {
      filters.product = [record.product.id];
    }
    if (record.subproduct?.id) {
      filters.subProduct = [record.subproduct.id];
    }

    if (isSBOMScan(record) && sbomFlagsEnabled) {
      url = "/sbom";
    } else {
      filters[type === "resolved" ? "mitigatedByScanId" : "scan"] = [`${id}`];
      filters.environmentName = [record.environment];
      if (type === "resolved") {
        filters.issueResolved = [
          moment(record.createdAt).utc().format(),
          moment().utc().format(),
        ];
      }
      url = "/findings";
    }

    updateURLSearch(
      history,
      {
        filters,
        pagination: {
          page: 0,
        },
      },
      url,
    );
  };

  const disableResolvedRedirect =
    resolvedFindingsCount === 0 || record.scanStatus !== ScanStatus.COMPLETED;
  const disableNewFindingsCount =
    newFindingsCount === 0 || record.scanStatus !== ScanStatus.COMPLETED;
  return (
    <ACPopover
      className="scan-stats-popover"
      content={
        <table className="scan-stats-count-table">
          <tr>
            <td />
            <td>Deduplicated</td>
            <td>{formatNumber(duplicateFindingsCount, "long")}</td>
          </tr>
          <tr
            className={disableResolvedRedirect ? "" : "clickable"}
            onClick={() =>
              !disableResolvedRedirect && redirectToFindings("resolved")
            }
          >
            <td>
              <div className="scan-stats-resolved-bar" />
            </td>
            <td>Resolved</td>
            <td>{formatNumber(resolvedFindingsCount, "long")}</td>
          </tr>
          <tr
            className={disableNewFindingsCount ? "" : "clickable"}
            onClick={() =>
              !disableNewFindingsCount && redirectToFindings("new")
            }
          >
            <td>
              <div className="scan-stats-new-bar" />
            </td>
            <td>New</td>
            <td>{formatNumber(newFindingsCount, "long")}</td>
          </tr>
          <tr className="scan-stats-count-table-total-row">
            <td />
            <td>Total</td>
            <td>{formatNumber(totalFindingsCount, "long")}</td>
          </tr>
        </table>
      }
      placement="top"
      trigger="hover"
    >
      <div className="scan-stats-count">
        <div className="scan-stats-count-total">
          {formatNumber(totalFindingsCount)}
        </div>
        <div className="findings-sub-change-count">
          <div className="scan-stats-count-new">
            {formatNumber(newFindingsCount)}
          </div>
          <div className="scan-stats-new-arrow">
            <CaretUpIcon />
          </div>
          <div className="scan-stats-resolved-arrow">
            <CaretDownIcon />
          </div>
          <div className="scan-stats-count-resolved">
            {formatNumber(resolvedFindingsCount)}
          </div>
        </div>
      </div>
    </ACPopover>
  );
}
